import type { ButtonHTMLAttributes, ReactNode } from "react";
import { cn } from "../../lib/utils";
import { Button } from "../ui/button";
import { Card } from "../ui/card";
import { EmptyState } from "../ui/empty-state";

export type RosterRemovalRecord = { id: string; name: string; reason: string };

export function RosterListItem({
  active = false,
  className,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & { active?: boolean }) {
  return (
    <Button
      type="button"
      variant="ghost"
      className={cn(
        "grid h-auto w-full cursor-pointer grid-cols-[minmax(0,1fr)_auto] grid-rows-[auto_auto] gap-x-2 gap-y-0.5 border-0 border-b border-border bg-transparent px-3 py-2.5 text-left text-foreground outline-none transition-colors hover:bg-muted/30 focus-visible:z-10 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset",
        active && "bg-primary/10 hover:bg-primary/15",
        className,
      )}
      aria-pressed={active}
      {...props}
    />
  );
}

/**
 * Generic master-detail shell shared by the Characters / Locations / Timeline /
 * Themes guide panels: a PanelHeader over a roster list (with a "Removed"
 * sidebar) and a detail pane with a select-to-view fallback. The per-entity
 * roster-item content and detail body are supplied via render props, so each
 * entity keeps its own bespoke detail while the shell/list/removals are shared.
 */
export function RosterGuidePanel<T extends { id: string }>({
  ariaLabel,
  rosterAriaLabel,
  header,
  items,
  selectedId,
  onSelect,
  renderRosterItem,
  rosterEmpty,
  removals,
  renderDetail,
  detailFallback,
}: {
  ariaLabel: string;
  rosterAriaLabel: string;
  header: ReactNode;
  items: T[];
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  renderRosterItem: (item: T, isActive: boolean) => ReactNode;
  rosterEmpty: string;
  removals: RosterRemovalRecord[];
  renderDetail: (selected: T) => ReactNode;
  detailFallback: ReactNode;
}) {
  const selected =
    items.find((item) => item.id === selectedId) ?? items[0] ?? null;

  return (
    <section className="flex h-full min-h-0 min-w-0 flex-col overflow-hidden" aria-label={ariaLabel}>
      <Card className="m-1.5 h-full min-h-0 flex-1 gap-0 rounded-lg border border-border bg-card py-0 shadow-sm ring-0">
        {header}
        <div className="grid min-h-0 flex-1 grid-cols-1 border-t border-border min-[901px]:grid-cols-[minmax(200px,280px)_1fr]">
          <aside
            className="max-h-55 overflow-auto border-b border-border bg-muted/10 min-[901px]:max-h-none min-[901px]:border-r min-[901px]:border-b-0"
            aria-label={rosterAriaLabel}
          >
            {items.length === 0 ? (
              <EmptyState compact className="m-3" title={rosterEmpty} />
            ) : (
              items.map((item) => {
                const isActive = selected?.id === item.id;
                return (
                  <RosterListItem
                    key={item.id}
                    active={isActive}
                    onClick={() => onSelect(item.id)}
                  >
                    {renderRosterItem(item, isActive)}
                  </RosterListItem>
                );
              })
            )}
            {removals.length > 0 ? (
              <div className="border-t border-border pb-2">
                <h3 className="px-3 pt-2 text-xs font-medium">Removed</h3>
                <ul className="m-0 flex list-none flex-col gap-1.5 px-3 pb-2 pt-1.5">
                  {removals.slice(0, 8).map((record) => (
                    <li
                      key={record.id}
                      className="rounded-md bg-muted/20 px-2 py-1.5"
                    >
                      <strong className="text-xs">{record.name}</strong>
                      <p className="mt-0.5 text-xs text-muted-foreground">
                        {record.reason}
                      </p>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </aside>
          <div className="min-h-0 overflow-auto px-[1.15rem] pb-6 pt-4">
            {selected ? (
              renderDetail(selected)
            ) : (
              <p className="p-4 text-sm text-muted-foreground">
                {detailFallback}
              </p>
            )}
          </div>
        </div>
      </Card>
    </section>
  );
}
